import { Card, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderDetailsMessages } from "@/messages/order-details/order-details.messages";

import BillingInformation from "./components/billingInformation";
import Header from "./components/orderDetails";
import OrderItemList from "./components/orderItemList";
import OrderObservation from "./components/orderObservation";
import OrderShipping from "./components/orderShipping";
import OrderSummary from "./components/orderSummary";
import PaymentDetails from "./components/paymentDetails";
import TrackOrder from "./components/trackOrder";

type Props = {
  messages: OrderDetailsMessages;
  order: IOrder | null;
  error?: Error;
  isPrint: boolean;
  componentToPrintRef?: React.RefObject<HTMLDivElement>;
};

function OrderDetailsTab({ messages, order, error, isPrint, componentToPrintRef }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();

  const shouldLoadError = !order && !!error;

  const onSiteRedirect = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    window.location.href = "https://talk2buy.com.br/#hero";
  };

  return (
    <MDBox my={isPrint ? 0 : 2}>
      <Grid container spacing={3} justifyContent="center">
        <Grid size={{ xs: 12, lg: 12 }}>
          <Card>
            <div ref={componentToPrintRef}>
              <MDBox pt={2} px={2}>
                <Header messages={messages} order={order} isPrint={isPrint} />
              </MDBox>
              <Divider />
              <MDBox pt={isPrint ? 0 : 1} pb={isPrint ? 0 : 3} px={2}>
                <MDBox>
                  <OrderShipping messages={messages} order={order} />
                </MDBox>

                <Divider />

                <MDBox mb={3}>
                  <OrderItemList messages={messages} order={order} />
                </MDBox>
                <Divider />

                <MDBox mt={3}>
                  <Grid container spacing={3}>
                    {!isPrint && (
                      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                        <TrackOrder messages={messages} order={order} />
                      </Grid>
                    )}

                    <Grid size={{ xs: isPrint ? 5 : 12, md: 6, lg: 5 }}>
                      <PaymentDetails messages={messages} order={order} isPrint={isPrint} />

                      <MDBox mt={3}>
                        {isPrint ? (
                          <OrderObservation messages={messages} order={order} />
                        ) : (
                          <BillingInformation messages={messages} order={order} />
                        )}
                      </MDBox>
                    </Grid>

                    <Grid size={{ xs: isPrint ? 5 : 12, lg: 3 }} sx={{ ml: "auto" }}>
                      <OrderSummary messages={messages} order={order} />
                    </Grid>
                  </Grid>
                </MDBox>

                {!isPrint && (
                  <MDBox display="flex" justifyContent="flex-end" mt={1} gap={2}>
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={() => router(ROUTE_URLS.orders(lang))}
                    >
                      {messages.backButton}
                    </MDButton>
                  </MDBox>
                )}

                {isPrint && (
                  <MDBox display="flex" mt={4} sx={{ display: "flex", alignItems: "center" }}>
                    <MDTypography variant="body2">{messages.orderGeneratedBy}</MDTypography>
                    <a
                      style={{ marginLeft: "5px", cursor: "pointer", fontWeight: "bold" }}
                      onClick={onSiteRedirect}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MDTypography
                        variant="body2"
                        color="dark"
                        fontWeight={"bold"}
                        style={{ cursor: "pointer" }}
                      >
                        {messages.talk2buy}
                      </MDTypography>
                    </a>
                  </MDBox>
                )}
              </MDBox>
              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.errorLoadingOrderDetails}
                  description={"error.message"}
                  alt={messages.orderNotFoundAlt}
                />
              )}
            </div>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
export default OrderDetailsTab;
